import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, CheckCircle2, Clock, FileText, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SubscriptionPlan, formatCurrency } from '../data/subscriptionPlans';
import { useTheme } from '../contexts/ThemeContext';
interface ProActivationSuccessScreenProps {
  plan: SubscriptionPlan;
  onComplete: () => void;
}
export function ProActivationSuccessScreen({
  plan,
  onComplete
}: ProActivationSuccessScreenProps) {
  const {
    isLight
  } = useTheme();
  const billingStart = new Date();
  billingStart.setDate(billingStart.getDate() + 3);
  const billingStartDate = billingStart.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  return <div className={`h-full flex flex-col relative overflow-hidden ${isLight ? 'bg-slate-50' : 'bg-black'}`} data-id="element-2733" data-mp="20gky7ylg106c">
      <div className={`absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/10' : 'bg-vexu/5'}`} data-id="element-2734" data-mp="ozcqp4fi33xy" />
      <main className="flex-1 overflow-y-auto flex flex-col items-center justify-center text-center px-6 py-10 relative z-10" data-id="element-2735" data-mp="1wthph7lvnibv">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 280,
        damping: 18
      }} className="w-24 h-24 rounded-full bg-vexu grid place-items-center shadow-[0_0_40px_rgba(0,200,83,0.3)]" data-id="element-2736" data-mp="21qrn3ajf70as"><CheckCircle2 className="w-12 h-12 text-white" data-id="element-2737" data-mp="1rv7vv1fswpsd" /></motion.div>
        <motion.h1 initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className={`mt-7 text-2xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2738" data-mp="1e0dvfl4qz3gq">Your Vexu {plan.name} plan is active.</motion.h1>
        <p className={`mt-2 max-w-[280px] text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2739" data-mp="192m2cnsd9bay">Your AI assistant is ready to handle calls, capture details, and keep you in the loop.</p>

        <motion.section initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className={`w-full mt-7 rounded-2xl p-4 text-left border ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/5 border-white/10'}`} data-id="element-2740" data-mp="101xkfhzuqqss">
          <div className="flex items-start gap-3" data-id="element-2741" data-mp="2wzbn3sfkbd09"><div className={`w-9 h-9 rounded-xl grid place-items-center flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2742" data-mp="2s1a4t9pynhpu"><Calendar className="w-4 h-4" data-id="element-2743" data-mp="2o19kx9k3eebe" /></div><div data-id="element-2744" data-mp="2ny56ljcwy270"><p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2745" data-mp="3kq8quhx7f40f">3 days free — then {formatCurrency(plan.price)}/mo</p><p className={`mt-1 text-xs leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2746" data-mp="3fqw6q4xpwb1j">Your subscription begins on {billingStartDate}. Cancel any time before then and you won’t be charged.</p></div></div>
        </motion.section>

        <div className="w-full mt-4 space-y-3 text-left" data-id="element-2747" data-mp="36sk7jeisc4xh">
          {[{
          icon: Clock,
          title: `${plan.minutes.toLocaleString()} Minutes`,
          detail: 'Included each month'
        }, {
          icon: Sparkles,
          title: `${formatCurrency(plan.overageRate)}/min overage`,
          detail: 'Only after your included minutes are used'
        }, {
          icon: FileText,
          title: 'Premium AI features',
          detail: 'Custom behavior, summaries, and multilingual support'
        }].map((item, index) => <motion.div key={item.title} initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.35 + index * 0.08
        }} className={`flex items-center gap-3 p-3.5 rounded-2xl ${isLight ? 'bg-white/80 border border-slate-100' : 'bg-white/5 border border-white/10'}`} data-id="element-2748" data-mp="ju6ppknufjtj" data-mpi={index}><div className={`w-9 h-9 rounded-xl grid place-items-center flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2749" data-mp="tm41otmoeftb"><item.icon className="w-4 h-4" data-id="element-2750" data-mp="13hnv99drp2ew" /></div><div data-id="element-2751" data-mp="13ip7hh6iruy7"><p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2752" data-mp="13ip5jbmylkvk">{item.title}</p><p className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2753" data-mp="17ipxlz2zdul3">{item.detail}</p></div></motion.div>)}
        </div>
      </main>
      <div className="px-6 pb-9 relative z-10" data-id="element-2754" data-mp="39acgfdcpyj6h"><Button fullWidth onClick={onComplete} className="py-3.5 flex items-center justify-center gap-2" data-id="element-2755" data-mp="39be4cieu305m">Start using Vexu <ArrowRight className="w-4 h-4" data-id="element-2756" data-mp="2dyw815ae2j7z" /></Button></div>
    </div>;
}